/* CYME CUSTOM START: purpose-limited exact-variant restock requests (#130).
   No onsite runtime, cookies, storage, analytics or private credentials.
   Source caller stays disabled pending #130/#373 activation and release gates. */
(() => {
  if (customElements.get('cyme-back-in-stock')) return;

  class CymeBackInStock extends HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      this.listeners = new AbortController();
      this.attempts ||= new Map();
      this.nativeForm = document.forms[this.dataset.formId];
      this.form = this.querySelector('form');
      this.fieldset = this.querySelector('fieldset');
      this.email = this.querySelector('[name="email"]');
      this.submitButtons = this.querySelectorAll('[type="submit"]');
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.context = this.querySelector('[data-alert-context]');
      this.status = this.querySelector('[data-alert-status]');
      try { this.initialVariant = JSON.parse(this.dataset.initialVariant); } catch { this.initialVariant = null; }
      if (!this.nativeForm || !this.form || !this.fieldset || !this.email) return;
      const options = { signal: this.listeners.signal };
      this.form.addEventListener('submit', event => this.send(event), options);
      this.nativeForm.addEventListener('variant:change', () => this.sync(), options);
      this.email.addEventListener('input', () => this.syncButton(), options);
      this.details.addEventListener('toggle', () => {
        this.sync();
        if (this.details.open && !this.hidden) this.email.focus();
        else if (!this.hidden && this.contains(document.activeElement)) this.summary.focus();
      }, options);
      this.fieldset.disabled = false;
      this.sync();
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.request?.abort();
      this.listeners = null;
    }

    selection() {
      const picker = document.querySelector(`variant-picker[form-id="${CSS.escape(this.dataset.formId)}"]`);
      if (picker?.selectionPending) return null;
      const variant = picker ? picker.selectedVariant : this.initialVariant;
      const nativeId = this.nativeForm?.querySelector('[name="id"]')?.value;
      if (!variant || String(variant.id) !== String(nativeId) || variant.available !== false || !variant.inventory_management || variant.inventory_policy !== 'deny') return null;
      return variant;
    }

    label(variant) {
      return this.dataset.productTitle + (variant.title && variant.title !== 'Default Title' ? ` · ${variant.title}` : '');
    }

    key(variant) { return `${this.email.value.trim().toLowerCase()}|${variant.id}`; }

    syncButton() {
      const variant = this.selection();
      for (const button of this.submitButtons) button.disabled = this.busy || !variant || this.attempts.has(this.key(variant));
    }

    sync() {
      const variant = this.selection();
      this.hidden = !variant;
      if (!variant) {
        this.details.open = false;
      } else {
        if (this.variantId !== variant.id) {
          this.details.open = false;
          if (!this.busy) this.status.textContent = '';
        }
        this.variantId = variant.id;
        this.context.textContent = `${this.dataset.selectedLabel} ${this.label(variant)}. ${this.dataset.soldOutLabel}.`;
      }
      this.syncButton();
    }

    async post(path, data) {
      this.request = new AbortController();
      const timer = setTimeout(() => this.request.abort(), 15000);
      try {
        const response = await fetch(`https://a.klaviyo.com/client/${path}?company_id=${encodeURIComponent(this.dataset.publicKey)}`, {
          method: 'POST', credentials: 'omit', referrerPolicy: 'no-referrer',
          headers: { accept: 'application/vnd.api+json', 'content-type': 'application/vnd.api+json', revision: '2026-07-15' },
          body: JSON.stringify({ data }), signal: this.request.signal
        });
        if (response.status !== 202) {
          const error = new Error('Request not accepted');
          error.explicit = response.status >= 400 && response.status < 500;
          throw error;
        }
      } finally {
        clearTimeout(timer);
        this.request = null;
      }
    }

    async send(event) {
      event.preventDefault();
      const variant = this.selection();
      if (this.busy || !variant || this.attempts.has(this.key(variant)) || !this.form.reportValidity()) return;
      const key = this.key(variant);
      const label = this.label(variant);
      const email = this.email.value.trim();
      const locale = this.dataset.locale === 'da-DK' ? 'da-DK' : 'en';
      // Only the explicitly labelled combined action is a new marketing opt-in.
      // Missing/stock-only submitter never changes marketing or tracking consent.
      const wantsNewsletter = event.submitter?.dataset.alertChoice === 'combined' && this.form.contains(event.submitter) && Boolean(this.dataset.newsletterListId);
      this.busy = true;
      this.fieldset.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      this.status.textContent = '';
      let alertAccepted = false;
      try {
        await this.post('profiles', { type: 'profile', attributes: { email, locale } });
        // A pending/new choice is not authorization for the previously confirmed variant.
        if (!this.isConnected || String(this.selection()?.id) !== String(variant.id)) {
          this.status.textContent = this.dataset.changed;
          return;
        }
        this.attempts.set(key, 'pending');
        await this.post('back-in-stock-subscriptions', {
          type: 'back-in-stock-subscription',
          attributes: { channels: ['EMAIL'], profile: { data: { type: 'profile', attributes: { email } } } },
          relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variant.id}` } } }
        });
        alertAccepted = true;
        this.attempts.set(key, 'accepted');
        if (!this.isConnected) return;
        this.status.textContent = `${label}. ${this.dataset.success}`;
        if (wantsNewsletter) {
          await this.post('subscriptions', {
            type: 'subscription',
            attributes: {
              custom_source: `CYME PDP stock alert + email newsletter / ${this.form.dataset.consentVersion} / ${locale}`,
              profile: { data: { type: 'profile', attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } }
            },
            relationships: { list: { data: { type: 'list', id: this.dataset.newsletterListId } } }
          });
          this.status.textContent = `${label}. ${this.dataset.success} ${this.dataset.combinedSuccess}`;
        }
      } catch (error) {
        if (!this.isConnected) return;
        if (alertAccepted) {
          this.status.textContent = `${label}. ${this.dataset.success} ${this.dataset.newsletterUnknown}`;
        } else {
          if (error.explicit) this.attempts.delete(key);
          else this.attempts.set(key, 'unknown');
          this.status.textContent = `${label}. ${error.explicit ? this.dataset.error : this.dataset.unknown}`;
        }
      } finally {
        this.busy = false;
        this.fieldset.disabled = false;
        this.form.removeAttribute('aria-busy');
        this.syncButton();
      }
    }
  }
  customElements.define('cyme-back-in-stock', CymeBackInStock);
})();
/* CYME CUSTOM END: purpose-limited exact-variant restock requests (#130) */
